import { useState, useRef, useEffect, useMemo } from 'react';
import { useStadtList } from '../../api/stadt';
import type { Stadt } from '../../types/stadt';
import { StadtModal } from './StadtModal';

interface StadtMultiSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
}

export function StadtMultiSelect({
  value,
  onChange,
  label,
  placeholder = 'Stadt hinzufügen…',
  error,
  disabled = false,
  id,
}: StadtMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStadt, setEditingStadt] = useState<Stadt | undefined>(undefined);
  const [initialName, setInitialName] = useState('');
  const [pendingCities, setPendingCities] = useState<Stadt[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: cities, isLoading } = useStadtList(true);

  const allCities = useMemo(() => {
    const byId = new Map<string, Stadt>();
    for (const c of cities ?? []) byId.set(c.id, c);
    for (const p of pendingCities) byId.set(p.id, p);
    return [...byId.values()];
  }, [cities, pendingCities]);

  const selectedCities = useMemo(() => {
    return value
      .map((id) => allCities.find((c) => c.id === id))
      .filter((c): c is Stadt => Boolean(c));
  }, [value, allCities]);

  useEffect(() => {
    if (pendingCities.length === 0) return;
    const stillPending = pendingCities.filter((p) => !cities?.some((c) => c.id === p.id));
    if (stillPending.length !== pendingCities.length) {
      setPendingCities(stillPending);
    }
  }, [cities, pendingCities]);

  const filteredCities = useMemo(() => {
    const available = allCities.filter((c) => !value.includes(c.id));
    if (!searchText.trim()) return available;
    const search = searchText.toLowerCase().trim();
    return available.filter((city) => city.name.toLowerCase().includes(search));
  }, [allCities, value, searchText]);

  const exactMatchExists = useMemo(() => {
    if (!searchText.trim()) return true;
    const search = searchText.toLowerCase().trim();
    return allCities.some((city) => city.name.toLowerCase() === search);
  }, [allCities, searchText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchText('');
    } else if (e.key === 'Backspace' && !searchText && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (e.key === 'Enter' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) setIsOpen(true);
  };

  const handleAddCity = (city: Stadt) => {
    if (!value.includes(city.id)) {
      onChange([...value, city.id]);
    }
    setSearchText('');
    inputRef.current?.focus();
  };

  const handleRemoveCity = (cityId: string) => {
    onChange(value.filter((id) => id !== cityId));
  };

  const handleEditCity = (e: React.MouseEvent, city: Stadt) => {
    e.stopPropagation();
    setEditingStadt(city);
    setInitialName('');
    setIsOpen(false);
    setModalOpen(true);
  };

  const handleCreateCity = () => {
    setEditingStadt(undefined);
    setInitialName(searchText.trim());
    setIsOpen(false);
    setModalOpen(true);
  };

  const handleModalSuccess = (stadt: Stadt) => {
    setPendingCities((prev) => [...prev.filter((p) => p.id !== stadt.id), stadt]);
    if (!value.includes(stadt.id)) {
      onChange([...value, stadt.id]);
    }
    setSearchText('');
    setIsOpen(false);
  };

  const inputClassName = `
    block w-full rounded-md shadow-sm sm:text-sm border
    ${error
      ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500'
      : 'border-gray-300 focus-within:border-primary-500 focus-within:ring-primary-500'
    }
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
    px-2 py-1 min-h-[38px] flex flex-wrap gap-1 items-center
  `;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className={inputClassName} onClick={() => !disabled && inputRef.current?.focus()}>
        {selectedCities.map((city) => (
          <span
            key={city.id}
            className="inline-flex items-center gap-1 rounded-full bg-primary-50 text-primary-700 text-xs px-2 py-0.5"
          >
            {city.name}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCity(city.id);
                }}
                className="text-primary-500 hover:text-primary-800"
                aria-label={`${city.name} entfernen`}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={selectedCities.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[80px] border-0 p-0 focus:ring-0 text-sm bg-transparent outline-none disabled:bg-gray-100"
          autoComplete="off"
        />
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Laden…</div>
          ) : (
            <>
              {filteredCities.length > 0 ? (
                <ul className="py-1">
                  {filteredCities.map((city) => (
                    <li key={city.id}>
                      <div
                        className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleAddCity(city)}
                      >
                        <span className="text-sm">{city.name}</span>
                        <button
                          type="button"
                          onClick={(e) => handleEditCity(e, city)}
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Stadt bearbeiten"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">Keine weiteren Städte verfügbar</div>
              )}

              {searchText.trim() && !exactMatchExists && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCreateCity}
                    className="w-full flex items-center px-4 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    "{searchText.trim()}" erstellen
                  </button>
                </div>
              )}

              {(!searchText.trim() || exactMatchExists) && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCreateCity}
                    className="w-full flex items-center px-4 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Neue Stadt erstellen
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <StadtModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleModalSuccess}
        editingStadt={editingStadt}
        initialName={initialName}
      />
    </div>
  );
}
